import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { format, parseISO } from 'date-fns';
import ConsultationDetailModal from './ConsultationDetailModal';

const ConsultationGrid = ({ 
  bookings, 
  selectedBookings, 
  onSelectBooking, 
  onSelectAll, 
  onStatusUpdate,
  onRefresh 
}) => {
  const [selectedConsultation, setSelectedConsultation] = useState(null);

  const getStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'confirmed': 'bg-[#D1F5F0] text-[#1FB5A5] border-[#1FB5A5]/30',
      'completed': 'bg-green-100 text-green-800 border-green-200',
      'cancelled': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors?.[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'pending': 'Clock',
      'confirmed': 'CheckCircle',
      'completed': 'CheckCheck',
      'cancelled': 'XCircle'
    };
    return icons?.[status] || 'Circle';
  };

  const formatDateTime = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy • h:mm a');
    } catch {
      return 'Invalid date';
    }
  };

  const handleViewDetails = (booking) => {
    setSelectedConsultation(booking);
  };

  const handleCloseModal = () => {
    setSelectedConsultation(null);
  };

  if (bookings?.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-elevation-1 p-12 text-center">
        <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No consultations found</h3>
        <p className="text-muted-foreground mb-6">No consultations match your current filters.</p>
        <Button onClick={onRefresh} iconName="RefreshCw" iconPosition="left">
          Refresh
        </Button>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white rounded-xl shadow-elevation-1 overflow-hidden">
        {/* Table Header */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-4 text-left">
                  <input
                    type="checkbox"
                    checked={selectedBookings?.length === bookings?.length && bookings?.length > 0}
                    onChange={onSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                  />
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Consultation
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Scheduled
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Service
                </th>
                <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {bookings?.map((booking) => (
                <tr 
                  key={booking?.id}
                  className="hover:bg-gray-50 transition-colors duration-150"
                >
                  <td className="px-6 py-4">
                    <input
                      type="checkbox"
                      checked={selectedBookings?.includes(booking?.id)}
                      onChange={() => onSelectBooking(booking?.id)}
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{booking?.clientName}</p>
                      <p className="text-xs text-muted-foreground">{booking?.clientEmail}</p>
                      {booking?.company && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <Icon name="Building2" size={12} />
                          {booking?.company}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{booking?.title}</p>
                      {booking?.description && (
                        <p className="text-xs text-muted-foreground line-clamp-1">{booking?.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <Icon name="Calendar" size={14} className="text-muted-foreground" />
                      <span className="text-sm text-foreground">{formatDateTime(booking?.scheduledStart)}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Duration: {booking?.durationMinutes} min
                    </p>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(booking?.status)}`}>
                      <Icon name={getStatusIcon(booking?.status)} size={14} />
                      {booking?.status?.charAt(0)?.toUpperCase() + booking?.status?.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-foreground">
                      {booking?.serviceInterest || 'Not specified'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(booking)}
                        iconName="Eye"
                        iconPosition="left"
                      >
                        View
                      </Button>
                      {booking?.status === 'pending' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onStatusUpdate(booking?.id, 'confirmed')}
                        >
                          Confirm
                        </Button>
                      )}
                      {booking?.status === 'confirmed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => onStatusUpdate(booking?.id, 'completed')}
                        >
                          Complete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Consultation Detail Modal */}
      {selectedConsultation && (
        <ConsultationDetailModal
          consultation={selectedConsultation}
          onClose={handleCloseModal}
          onStatusUpdate={onStatusUpdate}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
};

export default ConsultationGrid;